/**
 * 『ディメンション』 (Dimension)
 * 作詞・作曲・編曲：柊マグネタイト / 映像：まるいち / タイポグラフィ：ZUMA / 歌唱：ヰ世界情緒
 * Official MV: https://youtu.be/-x1Tyg2uvVM (#14)
 *
 * ジェネラティブアート・コンセプト（1シーン完結・歌詞表示なし）:
 * - MV全編を貫く「琥珀・シャンパンゴールドと深淵の青黒のコントラスト」「天へ続く白亜の円柱と螺旋階段」
 *   「深紅のポピーの花畑と花影が落ちる巨大な懐中時計盤」「天頂ドームへ聳える大聖堂の光窓と白い幾何学花紋」
 *   「湾曲する星辰の地平線と浮遊する城塞島」を、精巧な建築パースと光学演出で表現。
 * - 「🎲 転生（再生成）」ボタンを押すたびに、MVの象徴的な4つの次元（Dimension）モードが切り替わる：
 *   0. Mode 0 [白亜の円柱回廊と黄金星雲を昇る螺旋階段 (Spiral Staircase & Classical Pillars)]:
 *      渦巻く金色の星雲靄の中、幾重にも聳えるフルーティング円柱群と、繊細な手すり柵を備えた3D螺旋階段が空へと連なる。
 *   1. Mode 1 [深紅のポピー花畑と陽だまりの大時計盤 (Scarlet Poppy Field & Sunlit Clock Dial)]:
 *      黄金の光に照らされた巨大なアンティーク時計盤の上にポピーの花影が揺れ、周囲に鮮やかな深紅のポピー（虞美人草）が咲き誇る。
 *   2. Mode 2 [円筒大聖堂の光窓回廊と白き幾何学花紋 (Cathedral Rotunda Windows & White Rosettes)]:
 *      天頂の円形ドーム天窓へと湾曲しながら聳える無数の光るアーチ窓と、空間に浮かび上がる白い幾何学花紋・万華鏡グリッド。
 *   3. Mode 3 [星辰の湾曲地平線と浮遊城塞・黄金の十字星 (Cosmic Horizon, Floating Citadel & Cross-Star)]:
 *      青黒き宇宙の星空の下、黄金に輝く湾曲地平線と水面反射、天頂から地平線を貫く十字星、宙に浮かぶ逆さ尖塔の城塞島。
 */

export class DimensionGenerator {
  constructor(p) {
    this.p = p;
    this.time = 0;
    this.modeIndex = -1; // 初回initで決定、再生成（onReroll）で4つのMV次元モードを切り替え

    // Mode 0: 白亜の円柱群と螺旋階段ステップ
    this.pillars = [];
    this.spiralSteps = [];
    // Mode 1: 深紅のポピー花群
    this.poppies = [];
    // Mode 2: 大聖堂の幾何学花紋（ロゼット）グリッド
    this.rosetteGrid = [];
    // Mode 3: 星空と星雲ダスト
    this.stars = [];
    this.nebulaClouds = [];
    // 共通：立ち昇るシャンパンゴールドの光粒＆深紅の花弁パーティクル
    this.lightMotifParticles = [];
    // クリック時の次元アストロラーベ波紋（dimension_sb2.jpg Row 0 Col 0 / dimension_sb9.jpg Row 2）
    this.dimensionalRipples = [];
    // カーソル移動軌跡
    this.cursorTrail = [];

    this.pointerX = 0;
    this.pointerY = 0;
    this.parallaxX = 0;
    this.parallaxY = 0;
    this.flareBoost = 0;
  }

  onReroll() {
    this.modeIndex = (this.modeIndex + 1) % 4;
    this.flareBoost = 1.6;
  }

  init(params) {
    const p = this.p;
    const w = p.width || 800;
    const h = p.height || 600;
    const density = params ? params.density : 0.5;
    const scale = Math.min(w, h) / 720;

    if (this.modeIndex < 0) {
      this.modeIndex = Math.floor(p.random(4));
    }

    this.pointerX = w * 0.5;
    this.pointerY = h * 0.5;
    this.pillars = [];
    this.spiralSteps = [];
    this.poppies = [];
    this.rosetteGrid = [];
    this.stars = [];
    this.nebulaClouds = [];
    this.lightMotifParticles = [];
    this.dimensionalRipples = [];
    this.cursorTrail = [];

    // 1. Mode 0用：遠近3レイヤーの古典フルーティング円柱群（dimension_maxresdefault.jpg）
    const pillarConfigs = [
      { xRatio: 0.07, width: 68 * scale, depth: 1.45, layer: 2 },
      { xRatio: 0.16, width: 84 * scale, depth: 1.15, layer: 1 },
      { xRatio: 0.34, width: 44 * scale, depth: 0.65, layer: 0 },
      { xRatio: 0.66, width: 48 * scale, depth: 0.72, layer: 0 },
      { xRatio: 0.84, width: 78 * scale, depth: 1.2, layer: 1 },
      { xRatio: 0.94, width: 64 * scale, depth: 1.5, layer: 2 }
    ];
    for (let i = 0; i < pillarConfigs.length; i++) {
      this.pillars.push({
        ...pillarConfigs[i],
        ringOffset: p.random(0.15, 0.35)
      });
    }

    // 螺旋階段のステップデータ生成（左下から右上へ優雅にうねる3D螺旋階段）
    const stepCount = 54;
    for (let i = 0; i < stepCount; i++) {
      this.spiralSteps.push({
        index: i,
        t: i / (stepCount - 1)
      });
    }

    // 2. Mode 1用：深紅のポピー（虞美人草）の花畑（dimension_sb3.jpg / dimension_sb7.jpg Row 1）
    const poppyCount = Math.floor(26 + density * 24);
    for (let i = 0; i < poppyCount; i++) {
      // 時計盤（中央〜やや下）の周囲を取り囲むように配置し、数輪は前景で大きく咲かせる
      const angle = p.map(i, 0, poppyCount, 0, p.TWO_PI) + p.random(-0.2, 0.2);
      const isForeground = i < 6;
      const distX = isForeground ? p.random(w * 0.28, w * 0.46) : p.random(w * 0.22, w * 0.52);
      const distY = isForeground ? p.random(h * 0.22, h * 0.42) : p.random(h * 0.18, h * 0.46);
      this.poppies.push({
        x: w * 0.5 + Math.cos(angle) * distX,
        y: h * 0.54 + Math.sin(angle) * distY,
        size: (isForeground ? p.random(52, 84) : p.random(22, 46)) * scale,
        rot: p.random(p.TWO_PI),
        swayPhase: p.random(p.TWO_PI),
        swaySpeed: p.random(1.1, 2.2),
        petalTone: p.random(0.82, 1.08),
        depth: isForeground ? p.random(1.1, 1.6) : p.random(0.4, 0.95),
        isForeground
      });
    }

    // 3. Mode 2用：白い幾何学花紋（ロゼット）万華鏡グリッド（dimension_sb4.jpg Row 0 Col 2）
    const cols = 11;
    const rows = 7;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        this.rosetteGrid.push({
          u: (c + 0.5) / cols,
          v: (r + 0.5) / rows,
          phase: (r * 0.45 + c * 0.35),
          size: p.random(18, 28) * scale
        });
      }
    }

    // 4. 星空と黄金星雲靄（Nebula）パーティクル（dimension_maxresdefault.jpg / dimension_maxres1.jpg）
    const starCount = Math.floor(85 + density * 90);
    for (let i = 0; i < starCount; i++) {
      this.stars.push({
        x: p.random(w),
        y: p.random(h * 0.82),
        size: p.random(1.2, 4.5) * scale,
        twinkleSpeed: p.random(1.5, 4.8),
        phase: p.random(p.TWO_PI),
        isBokeh: i % 6 === 0,
        depth: p.random(0.2, 0.85)
      });
    }

    const cloudCount = 16;
    for (let i = 0; i < cloudCount; i++) {
      this.nebulaClouds.push({
        x: p.random(w),
        y: p.random(h * 0.12, h * 0.85),
        w: p.random(180, 420) * scale,
        h: p.random(65, 160) * scale,
        angle: p.random(-0.35, 0.35),
        speed: p.random(0.15, 0.45) * (i % 2 === 0 ? 1 : -1),
        alpha: p.random(18, 46),
        phase: p.random(p.TWO_PI)
      });
    }

    // 5. 立ち昇るシャンパンゴールドの光粒と深紅の花弁ダスト
    const particleCount = Math.floor(60 + density * 65);
    for (let i = 0; i < particleCount; i++) {
      this.lightMotifParticles.push({
        x: p.random(w),
        y: p.random(h),
        vx: p.random(-0.5, 0.5) * scale,
        vy: p.random(-1.6, -0.35) * scale,
        size: p.random(2.2, 7.5) * scale,
        isCrimsonPetal: i % 5 === 0,
        rot: p.random(p.TWO_PI),
        rotSpeed: p.random(-0.04, 0.04),
        alpha: p.random(110, 245),
        phase: p.random(p.TWO_PI),
        depth: p.random(0.4, 1.4)
      });
    }
  }

  onPointerDown(x, y) {
    const p = this.p;
    const scale = Math.min(p.width, p.height) / 720;
    this.flareBoost = 1.85;

    // クリック地点にアストロラーベ（天球儀）の幾何学目盛り波紋を展開（dimension_sb2.jpg Row 0 Col 0）
    this.dimensionalRipples.push({
      x,
      y,
      radius: 14 * scale,
      maxRadius: p.random(170, 280) * scale,
      rot: p.random(p.TWO_PI),
      alpha: 255
    });
    if (this.dimensionalRipples.length > 5) {
      this.dimensionalRipples.shift();
    }
  }

  onPointerMove(x, y) {
    const p = this.p;
    const scale = Math.min(p.width, p.height) / 720;
    this.pointerX = x;
    this.pointerY = y;

    this.cursorTrail.push({
      x: x + p.random(-8, 8) * scale,
      y: y + p.random(-8, 8) * scale,
      size: p.random(4, 11) * scale,
      isCrimson: this.modeIndex === 1 && p.frameCount % 3 === 0,
      alpha: 230
    });
    if (this.cursorTrail.length > 30) {
      this.cursorTrail.shift();
    }
  }

  draw(params) {
    const p = this.p;
    const w = p.width;
    const h = p.height;
    const motion = params ? params.motion : 0.5;
    const glow = params ? params.glow : 0.6;
    const scale = Math.min(w, h) / 720;

    const dt = Math.min(p.deltaTime || 16.67, 50) / 16.67;
    const speedMult = 0.45 + motion * 1.15;
    this.time += 0.015 * dt * speedMult;
    this.flareBoost = p.lerp(this.flareBoost, 0, 0.05 * dt);

    // マウス視差（パララックス）の滑らかな追従
    const targetPx = ((this.pointerX || w * 0.5) / w - 0.5) * 42;
    const targetPy = ((this.pointerY || h * 0.5) / h - 0.5) * 32;
    this.parallaxX = p.lerp(this.parallaxX, targetPx, 0.07);
    this.parallaxY = p.lerp(this.parallaxY, targetPy, 0.07);

    const cx = w * 0.5 - this.parallaxX * 0.4;
    const cy = h * 0.5 - this.parallaxY * 0.4;

    // =========================================================================
    // 再生成（Reroll）で切り替わる4つのMV次元世界モード
    // =========================================================================
    if (this.modeIndex === 0) {
      this.drawMode0SpiralStaircaseAndPillars(cx, cy, w, h, scale, dt, speedMult, glow);
    } else if (this.modeIndex === 1) {
      this.drawMode1ScarletPoppiesAndClockDial(cx, cy, w, h, scale, dt, speedMult, glow);
    } else if (this.modeIndex === 2) {
      this.drawMode2CathedralRotundaAndRosettes(cx, cy, w, h, scale, dt, speedMult, glow);
    } else {
      this.drawMode3CosmicHorizonAndFloatingCitadel(cx, cy, w, h, scale, dt, speedMult, glow);
    }

    // =========================================================================
    // 共通レイヤー：立ち昇る黄金粒子・アストロラーベ次元波紋・カーソル軌跡
    // =========================================================================
    this.updateAndDrawLightParticles(w, h, scale, dt, speedMult, glow);
    this.updateAndDrawDimensionalRipples(scale, dt, glow);
    this.updateAndDrawCursorTrail(scale, dt, glow);

    // MV特有のシネマスコープ上下黒帯
    this.drawCinemascopeLetterbox(w, h);
  }

  /**
   * Mode 0: 白亜の円柱回廊と黄金星雲を昇る螺旋階段（dimension_maxresdefault.jpg / dimension_maxres2.jpg / dimension_sb5.jpg）
   */
  drawMode0SpiralStaircaseAndPillars(cx, cy, w, h, scale, dt, speedMult, glow) {
    const p = this.p;
    const ctx = p.drawingContext;

    // 1. 深淵のセピアブラック背景と左下〜中央へ広がる琥珀・シャンパンゴールドの後光
    p.background(10, 8, 7);
    ctx.save();
    const bgGrad = ctx.createRadialGradient(
      w * 0.25 - this.parallaxX * 0.3,
      h * 0.78 - this.parallaxY * 0.3,
      20 * scale,
      cx,
      cy,
      Math.max(w, h) * 0.85
    );
    bgGrad.addColorStop(0, `rgba(255, 246, 210, ${0.95 * glow})`);
    bgGrad.addColorStop(0.28, `rgba(232, 195, 130, ${0.72 * glow})`);
    bgGrad.addColorStop(0.62, `rgba(92, 68, 42, ${0.48 * glow})`);
    bgGrad.addColorStop(1, 'rgba(12, 10, 9, 0.98)');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();

    // 2. 渦巻く金色の星雲靄（Nebula Mist）と星粒
    this.drawGoldenNebulaAndStars(w, h, scale, glow);

    // 3. 背後の螺旋階段トンネル渦（dimension_sb5.jpg Row 1 の象徴的な同心螺旋階段）
    this.drawBackgroundSpiralTunnel(cx, cy, scale, glow * 0.55);

    // 4. 遠景・中景の白亜フルーティング円柱群（layer 0, 1）
    this.drawClassicalPillars(w, h, scale, glow, [0, 1]);

    // 5. 空へと連なるメインの3D螺旋階段と手すり柵、階段を昇る光のシルエット（dimension_maxresdefault.jpg）
    this.drawMain3DSpiralStaircase(w, h, scale, glow);

    // 6. 前景のボケ味を帯びた巨大円柱（layer 2）
    this.drawClassicalPillars(w, h, scale, glow, [2]);
  }

  /**
   * 背後にうっすらと浮かび上がる同心円状の螺旋階段トンネル（dimension_sb5.jpg Row 1）
   */
  drawBackgroundSpiralTunnel(cx, cy, scale, alphaMult) {
    const p = this.p;
    p.push();
    p.translate(cx + 30 * scale, cy - 20 * scale);
    p.rotate(this.time * 0.15);

    const turns = 3.2;
    const totalSteps = 96;
    for (let i = 0; i < totalSteps; i++) {
      const t = i / totalSteps;
      const angle = t * p.TWO_PI * turns;
      const rInner = p.lerp(28, 280, t) * scale;
      const rOuter = rInner + p.lerp(18, 52, t) * scale;

      const x1 = Math.cos(angle) * rInner;
      const y1 = Math.sin(angle) * rInner;
      const x2 = Math.cos(angle) * rOuter;
      const y2 = Math.sin(angle) * rOuter;

      p.stroke(245, 222, 175, (125 * (1 - t * 0.45)) * alphaMult);
      p.strokeWeight(1.4 * scale);
      p.line(x1, y1, x2, y2);

      // 外周の手すりアーク
      const nextAngle = angle + (p.TWO_PI * turns) / totalSteps;
      p.stroke(85, 62, 38, 165 * alphaMult);
      p.strokeWeight(2.4 * scale);
      p.line(x2, y2, Math.cos(nextAngle) * rOuter, Math.sin(nextAngle) * rOuter);
    }

    // 中心の眩い消失点コア
    p.noStroke();
    p.fill(255, 248, 225, 195 * alphaMult);
    p.circle(0, 0, 62 * scale);
    p.pop();
  }

  /**
   * 幾重にも聳える白亜・黄金のフルーティング（縦溝）円柱群（dimension_maxresdefault.jpg）
   */
  drawClassicalPillars(w, h, scale, glow, allowedLayers) {
    const p = this.p;
    const ctx = p.drawingContext;

    for (let i = 0; i < this.pillars.length; i++) {
      const pl = this.pillars[i];
      if (!allowedLayers.includes(pl.layer)) continue;

      const px = pl.xRatio * w - this.parallaxX * pl.depth;
      const pw = pl.width;

      ctx.save();
      // 円柱の立体陰影グラデーション（左側から強いシャンパンゴールドの陽光を受ける）
      const colGrad = ctx.createLinearGradient(px - pw * 0.5, 0, px + pw * 0.5, 0);
      if (pl.layer === 2) {
        // 前景のシルエット気味な大円柱
        colGrad.addColorStop(0, `rgba(248, 224, 172, ${0.88 * glow})`);
        colGrad.addColorStop(0.28, 'rgba(135, 106, 72, 0.94)');
        colGrad.addColorStop(0.75, 'rgba(38, 28, 20, 0.96)');
        colGrad.addColorStop(1, 'rgba(82, 62, 42, 0.92)');
      } else {
        // 中景・遠景の白亜に輝く円柱
        colGrad.addColorStop(0, 'rgba(255, 250, 232, 0.98)');
        colGrad.addColorStop(0.38, 'rgba(235, 212, 168, 0.96)');
        colGrad.addColorStop(0.82, 'rgba(112, 88, 60, 0.94)');
        colGrad.addColorStop(1, 'rgba(168, 138, 98, 0.95)');
      }
      ctx.fillStyle = colGrad;
      ctx.fillRect(px - pw * 0.5, 0, pw, h);
      ctx.restore();

      // フルーティング（古典円柱特有の縦溝ストライプ）
      p.push();
      const fluteCount = 6;
      for (let f = 1; f < fluteCount; f++) {
        const fx = px - pw * 0.45 + (pw * 0.9 * f) / fluteCount;
        p.stroke(48, 34, 22, pl.layer === 2 ? 95 : 65);
        p.strokeWeight(1.1 * scale);
        p.line(fx, 0, fx, h);
      }

      // 円柱の節リング（柱頭・柱身の装飾モールディング帯 — dimension_maxresdefault.jpg）
      const ringYPositions = [h * 0.22, h * 0.25, h * 0.78, h * 0.81];
      p.rectMode(p.CENTER);
      for (let r = 0; r < ringYPositions.length; r++) {
        const ry = ringYPositions[r];
        p.noStroke();
        p.fill(255, 246, 218, 235);
        p.rect(px, ry, pw * 1.12, 5.5 * scale, 2 * scale);
        p.fill(68, 48, 30, 190);
        p.rect(px, ry + 3.5 * scale, pw * 1.1, 3.0 * scale);
      }
      p.pop();
    }
  }

  /**
   * 空へと連なるメインの3D螺旋階段・繊細な手すり支柱・昇りゆく光のシルエット（dimension_maxresdefault.jpg / dimension_maxres2.jpg）
   */
  drawMain3DSpiralStaircase(w, h, scale, glow) {
    const p = this.p;
    p.push();

    const handrailBackPts = [];
    const handrailFrontPts = [];

    // 階段のアニメーション進行オフセット
    const flowOffset = (this.time * 0.12) % (1 / this.spiralSteps.length);

    for (let i = 0; i < this.spiralSteps.length; i++) {
      const t = p.constrain(this.spiralSteps[i].t - flowOffset, 0, 1);

      // 左下から中央の円柱を回り込みながら右上へと昇るS字ヘリカル軌道
      const curvePhase = t * p.PI * 1.65 - 0.55;
      const sx =
        p.lerp(w * 0.16, w * 0.78, t) +
        Math.sin(curvePhase) * 135 * scale -
        this.parallaxX * p.lerp(1.1, 0.55, t);
      const sy =
        p.lerp(h * 0.94, h * 0.08, t) +
        Math.cos(curvePhase) * 28 * scale -
        this.parallaxY * p.lerp(1.1, 0.55, t);

      const stepW = p.lerp(115, 58, t) * scale;
      const stepH = p.lerp(11, 5.5, t) * scale;
      const stepSkew = Math.cos(curvePhase) * 22 * scale;

      // ステップ下面の立体シャドウ
      p.noStroke();
      p.fill(42, 30, 18, 225);
      p.quad(
        sx - stepW * 0.5,
        sy + stepH,
        sx + stepW * 0.5,
        sy + stepH - stepSkew * 0.35,
        sx + stepW * 0.5 + 8 * scale,
        sy + stepH * 2.1 - stepSkew * 0.35,
        sx - stepW * 0.5 + 8 * scale,
        sy + stepH * 2.1
      );

      // ステップ上面・側面（左下ほど黄金の逆光で白く輝く）
      const lightIntensity = Math.pow(1 - t, 1.3) * glow + this.flareBoost * 0.25;
      p.fill(
        p.lerp(195, 255, lightIntensity),
        p.lerp(168, 248, lightIntensity),
        p.lerp(122, 220, lightIntensity),
        242
      );
      p.stroke(255, 250, 230, 190);
      p.strokeWeight(0.9 * scale);
      p.quad(
        sx - stepW * 0.5,
        sy,
        sx + stepW * 0.5,
        sy - stepSkew * 0.35,
        sx + stepW * 0.5,
        sy + stepH - stepSkew * 0.35,
        sx - stepW * 0.5,
        sy + stepH
      );

      // ステップ両端から垂直に立ち上がる細い手すり支柱（バラスター）
      const postH = p.lerp(46, 24, t) * scale;
      const bx1 = sx - stepW * 0.46;
      const by1 = sy;
      const bx2 = sx + stepW * 0.46;
      const by2 = sy - stepSkew * 0.32;

      p.stroke(248, 232, 195, 210);
      p.strokeWeight(1.15 * scale);
      p.line(bx1, by1, bx1, by1 - postH);
      p.line(bx2, by2, bx2, by2 - postH);

      handrailFrontPts.push({ x: bx1, y: by1 - postH });
      handrailBackPts.push({ x: bx2, y: by2 - postH });
    }

    // 螺旋階段の連続する2本の手すりレール（上下2段の細い手すり線 — dimension_maxresdefault.jpg）
    p.noFill();
    for (const pts of [handrailFrontPts, handrailBackPts]) {
      p.stroke(255, 246, 218, 235);
      p.strokeWeight(1.8 * scale);
      p.beginShape();
      for (let i = 0; i < pts.length; i++) {
        p.vertex(pts[i].x, pts[i].y);
      }
      p.endShape();

      // 中段のサブ手すりワイヤー
      p.stroke(235, 208, 162, 165);
      p.strokeWeight(1.0 * scale);
      p.beginShape();
      for (let i = 0; i < pts.length; i++) {
        p.vertex(pts[i].x, pts[i].y + 11 * scale);
      }
      p.endShape();
    }

    // 階段の中央で黄金の粒子を纏いながら佇む・昇りゆく「光の少女シルエット」（dimension_maxresdefault.jpg 中央）
    const figIdx = Math.floor(this.spiralSteps.length * 0.46);
    if (handrailFrontPts[figIdx]) {
      const fx =
        (handrailFrontPts[figIdx].x + handrailBackPts[figIdx].x) * 0.5 +
        Math.sin(this.time * 2.0) * 3 * scale;
      const fy = (handrailFrontPts[figIdx].y + handrailBackPts[figIdx].y) * 0.5 + 8 * scale;

      p.noStroke();
      // 後光オーラ
      p.fill(255, 238, 185, 85 * glow);
      p.circle(fx, fy - 12 * scale, 95 * scale);
      p.fill(255, 250, 225, 248);
      // 頭部と風に揺れる外套のシルエット
      p.circle(fx, fy - 34 * scale, 18 * scale);
      p.beginShape();
      p.vertex(fx - 5 * scale, fy - 24 * scale);
      p.vertex(fx + 6 * scale, fy - 24 * scale);
      p.vertex(fx + 22 * scale + Math.sin(this.time * 3.2) * 4 * scale, fy + 6 * scale);
      p.vertex(fx - 18 * scale, fy + 10 * scale);
      p.endShape(p.CLOSE);
    }

    p.pop();
  }

  /**
   * Mode 1: 深紅のポピー花畑と陽だまりの大時計盤（dimension_sb2.jpg Row 2 Col 2 / dimension_sb3.jpg / dimension_sb7.jpg Row 1）
   */
  drawMode1ScarletPoppiesAndClockDial(cx, cy, w, h, scale, dt, speedMult, glow) {
    const p = this.p;
    const ctx = p.drawingContext;

    // 1. 深い宵闇の森と上部から差し込む琥珀・黄金の陽光グラデーション
    p.background(8, 7, 10);
    ctx.save();
    const skyGrad = ctx.createLinearGradient(0, 0, 0, h);
    skyGrad.addColorStop(0, `rgba(248, 225, 175, ${0.85 * glow})`);
    skyGrad.addColorStop(0.36, `rgba(145, 96, 62, ${0.48 * glow})`);
    skyGrad.addColorStop(0.72, 'rgba(18, 12, 16, 0.94)');
    skyGrad.addColorStop(1, 'rgba(6, 5, 8, 0.98)');
    ctx.fillStyle = skyGrad;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();

    // 2. 遠景の深紅のポピー群生（背後レイヤー）
    for (let i = 0; i < this.poppies.length; i++) {
      const pp = this.poppies[i];
      if (!pp.isForeground) {
        this.drawSingleScarletPoppy(pp, scale, glow);
      }
    }

    // 3. 中央に鎮座する「陽だまりの巨大アンティーク時計盤と揺れるポピーの花影」（dimension_sb3.jpg Row 1-2 完全再現）
    this.drawSunlitClockDialWithPoppyShadows(cx, cy + h * 0.06, w, h, scale, glow);

    // 4. 前景で鮮やかに揺れる大輪の深紅ポピー（dimension_sb7.jpg Row 1）
    for (let i = 0; i < this.poppies.length; i++) {
      const pp = this.poppies[i];
      if (pp.isForeground) {
        this.drawSingleScarletPoppy(pp, scale, glow);
      }
    }
  }

  /**
   * 黄金の光に照らされたアンティーク時計盤と、盤面に落ちるポピーの花影（dimension_sb3.jpg Row 1-2）
   */
  drawSunlitClockDialWithPoppyShadows(cx, cy, w, h, scale, glow) {
    const p = this.p;
    const dialRx = 255 * scale;
    const dialRy = 158 * scale; // パースペクティブのついた楕円時計盤

    p.push();
    p.translate(cx, cy);

    // 時計盤外周の黄金ハロー
    p.noStroke();
    p.fill(255, 232, 168, 52 * glow);
    p.ellipse(0, 0, dialRx * 2.25, dialRy * 2.25);

    // アンティーク・アイボリーの時計文字盤
    p.fill(254, 248, 215, 248);
    p.stroke(148, 112, 62, 220);
    p.strokeWeight(4.5 * scale);
    p.ellipse(0, 0, dialRx * 2, dialRy * 2);

    // 外周と内周の二重鉄道目盛りトラック（dimension_sb3.jpg Row 1-2 の特徴的なドット/ティック円環）
    const tickCount = 60;
    for (let i = 0; i < tickCount; i++) {
      const a = (p.TWO_PI / tickCount) * i;
      const isHour = i % 5 === 0;
      const r1 = isHour ? 0.84 : 0.88;
      const r2 = 0.93;
      p.stroke(62, 46, 28, isHour ? 235 : 175);
      p.strokeWeight((isHour ? 3.2 : 1.5) * scale);
      p.line(
        Math.cos(a) * dialRx * r1,
        Math.sin(a) * dialRy * r1,
        Math.cos(a) * dialRx * r2,
        Math.sin(a) * dialRy * r2
      );

      // 内側の小さな秒目盛りリング
      const ir1 = 0.56;
      const ir2 = isHour ? 0.61 : 0.59;
      p.strokeWeight((isHour ? 2.2 : 1.1) * scale);
      p.line(
        Math.cos(a) * dialRx * ir1,
        Math.sin(a) * dialRy * ir1,
        Math.cos(a) * dialRx * ir2,
        Math.sin(a) * dialRy * ir2
      );
    }

    p.noFill();
    p.stroke(78, 58, 34, 195);
    p.strokeWeight(1.8 * scale);
    p.ellipse(0, 0, dialRx * 1.86, dialRy * 1.86);
    p.ellipse(0, 0, dialRx * 1.12, dialRy * 1.12);

    // 盤面の上に落ちる「風に揺れるポピーの花々と茎のシルエット影」（dimension_sb3.jpg Row 1-2 の最重要ディテール！）
    p.noStroke();
    p.fill(58, 44, 32, 115);
    const shadowCount = 9;
    for (let s = 0; s < shadowCount; s++) {
      const sa = p.map(s, 0, shadowCount, p.PI * 0.12, p.PI * 0.92) + Math.sin(this.time * 1.6 + s) * 0.05;
      const sx1 = Math.cos(sa) * dialRx * 1.02;
      const sy1 = Math.sin(sa) * dialRy * 1.02;
      const sx2 = Math.cos(sa) * dialRx * 0.42;
      const sy2 = Math.sin(sa) * dialRy * 0.42;

      p.stroke(58, 44, 32, 110);
      p.strokeWeight(5.5 * scale);
      p.line(sx1, sy1, sx2, sy2);

      p.noStroke();
      p.push();
      p.translate(sx2, sy2);
      p.rotate(sa + p.HALF_PI);
      p.ellipse(-10 * scale, 0, 28 * scale, 16 * scale);
      p.ellipse(10 * scale, 0, 28 * scale, 16 * scale);
      p.ellipse(0, -8 * scale, 22 * scale, 22 * scale);
      p.pop();
    }

    // アンティーク時計の3本の針（短針・長針・極細の秒針）
    const hourAngle = -p.PI * 0.82 + this.time * 0.08;
    const minAngle = -p.PI * 0.42 + this.time * 0.45;
    const secAngle = p.PI * 0.22 + this.time * 2.2;

    // 針の落影
    p.stroke(45, 32, 18, 95);
    p.strokeWeight(6.5 * scale);
    p.line(4 * scale, 6 * scale, Math.cos(hourAngle) * dialRx * 0.54 + 4 * scale, Math.sin(hourAngle) * dialRy * 0.54 + 6 * scale);
    p.strokeWeight(4.5 * scale);
    p.line(4 * scale, 6 * scale, Math.cos(minAngle) * dialRx * 0.78 + 4 * scale, Math.sin(minAngle) * dialRy * 0.78 + 6 * scale);

    // 短針・長針本体
    p.stroke(32, 22, 14, 245);
    p.strokeWeight(6.0 * scale);
    p.line(
      -Math.cos(hourAngle) * dialRx * 0.14,
      -Math.sin(hourAngle) * dialRy * 0.14,
      Math.cos(hourAngle) * dialRx * 0.54,
      Math.sin(hourAngle) * dialRy * 0.54
    );

    p.strokeWeight(4.0 * scale);
    p.line(
      -Math.cos(minAngle) * dialRx * 0.18,
      -Math.sin(minAngle) * dialRy * 0.18,
      Math.cos(minAngle) * dialRx * 0.8,
      Math.sin(minAngle) * dialRy * 0.8
    );

    // 秒針
    p.stroke(48, 34, 22, 230);
    p.strokeWeight(1.8 * scale);
    p.line(
      -Math.cos(secAngle) * dialRx * 0.22,
      -Math.sin(secAngle) * dialRy * 0.22,
      Math.cos(secAngle) * dialRx * 0.88,
      Math.sin(secAngle) * dialRy * 0.88
    );

    // 中心の留め具（カナ）
    p.noStroke();
    p.fill(28, 18, 12, 250);
    p.ellipse(0, 0, 18 * scale, 13 * scale);
    p.fill(245, 215, 155, 240);
    p.ellipse(0, 0, 7 * scale, 5 * scale);

    p.pop();
  }

  /**
   * 鮮やかな深紅のポピー（虞美人草）1輪の描画（dimension_sb7.jpg Row 1 の黒い花芯と繊細な雄しべを忠実再現）
   */
  drawSingleScarletPoppy(pp, scale, glow) {
    const p = this.p;
    const swayX = Math.sin(this.time * pp.swaySpeed + pp.swayPhase) * 8 * scale;
    const swayY = Math.cos(this.time * pp.swaySpeed * 0.8 + pp.swayPhase) * 4 * scale;
    const x = pp.x + swayX - this.parallaxX * pp.depth;
    const y = pp.y + swayY - this.parallaxY * pp.depth;
    const r = pp.size;

    p.push();
    p.translate(x, y);
    p.rotate(pp.rot + Math.sin(this.time * pp.swaySpeed + pp.swayPhase) * 0.08);

    // 4枚の重なり合う深紅の花弁
    p.noStroke();
    for (let k = 0; k < 4; k++) {
      p.push();
      p.rotate((p.HALF_PI * k) + (k % 2 === 0 ? 0.15 : -0.15));
      const cRed = p.constrain((k % 2 === 0 ? 228 : 195) * pp.petalTone, 140, 255);
      const cGreen = k % 2 === 0 ? 26 : 16;
      const cBlue = k % 2 === 0 ? 38 : 28;
      p.fill(cRed, cGreen, cBlue, 238);
      p.ellipse(r * 0.24, 0, r * 0.72, r * 0.88);

      // 花弁内側の明るい朱紅グラデーションハイライト
      p.fill(255, 78, 65, 115 * glow);
      p.ellipse(r * 0.16, 0, r * 0.42, r * 0.52);
      p.pop();
    }

    // 中心を取り囲む繊細な黒紫の雄しべ（葯）リング（dimension_sb7.jpg Row 1）
    p.stroke(24, 10, 18, 220);
    p.strokeWeight(1.0 * scale);
    const stamenCount = 20;
    for (let s = 0; s < stamenCount; s++) {
      const sa = (p.TWO_PI / stamenCount) * s;
      const sr = r * (0.2 + (s % 2) * 0.05);
      p.line(0, 0, Math.cos(sa) * sr, Math.sin(sa) * sr);
      p.noStroke();
      p.fill(42, 18, 28, 235);
      p.circle(Math.cos(sa) * sr, Math.sin(sa) * sr, 2.5 * scale);
      p.stroke(24, 10, 18, 220);
    }

    // 漆黒〜濃紫の花芯（子房）
    p.noStroke();
    p.fill(16, 8, 14, 250);
    p.circle(0, 0, r * 0.24);

    p.pop();
  }

  /**
   * Mode 2: 円筒大聖堂の光窓回廊と白き幾何学花紋（dimension_maxres3.jpg / dimension_sb4.jpg / dimension_sb8.jpg / dimension_sb9.jpg）
   */
  drawMode2CathedralRotundaAndRosettes(cx, cy, w, h, scale, dt, speedMult, glow) {
    const p = this.p;
    const ctx = p.drawingContext;

    // 1. 重厚なティール・チャコールと琥珀光が交錯する大聖堂内部背景
    p.background(12, 16, 20);
    const domeX = cx;
    const domeY = -h * 0.12 - this.parallaxY * 0.3;

    // 2. 天頂の円形天窓（オクルス）ドームと放射状に広がる無数の光るアーチ窓ティア（dimension_sb4.jpg 完全再現）
    p.push();
    const tierCount = 6;
    const bayCount = 5; // 5つの大柱区画（ベイ）
    const baySpread = 0.24;

    for (let b = -2; b <= 2; b++) {
      const bayCenterAngle = p.HALF_PI + b * baySpread + Math.sin(this.time * 0.25) * 0.02;

      for (let t = 0; t < tierCount; t++) {
        const rTop = (210 + t * 105) * scale;
        const rBot = rTop + 76 * scale;
        const subWinCount = 4;
        const bayArc = baySpread * 0.82;
        const startA = bayCenterAngle - bayArc * 0.5;

        for (let sw = 0; sw < subWinCount; sw++) {
          const a1 = startA + (bayArc * sw) / subWinCount + 0.008;
          const a2 = startA + (bayArc * (sw + 1)) / subWinCount - 0.008;

          const x1 = domeX + Math.cos(a1) * rTop;
          const y1 = domeY + Math.sin(a1) * rTop;
          const x2 = domeX + Math.cos(a2) * rTop;
          const y2 = domeY + Math.sin(a2) * rTop;
          const x3 = domeX + Math.cos(a2) * rBot;
          const y3 = domeY + Math.sin(a2) * rBot;
          const x4 = domeX + Math.cos(a1) * rBot;
          const y4 = domeY + Math.sin(a1) * rBot;

          // 窓の黄金〜アイボリー発光（パルス明滅）
          const winPulse = 0.78 + 0.22 * Math.sin(this.time * 2.2 + b * 0.9 + t * 0.7 + sw);
          const isStainedAccent = t === tierCount - 1 && (sw + Math.abs(b)) % 2 === 0;
          if (isStainedAccent) {
            p.fill(255, 210, 145, 235 * winPulse * glow);
          } else {
            p.fill(255, 248, 222, 245 * winPulse * glow);
          }
          p.stroke(28, 34, 38, 220);
          p.strokeWeight(2.2 * scale);
          p.quad(x1, y1, x2, y2, x3, y3, x4, y4);

          // 窓内部の細かい桟（マリオン格子）
          p.stroke(42, 48, 52, 165);
          p.strokeWeight(1.1 * scale);
          p.line((x1 + x2) * 0.5, (y1 + y2) * 0.5, (x3 + x4) * 0.5, (y3 + y4) * 0.5);
          p.line((x1 + x4) * 0.5, (y1 + y4) * 0.5, (x2 + x3) * 0.5, (y2 + y3) * 0.5);
        }
      }

      // 大聖堂の巨大な垂直リブ柱（黄金のエッジハイライトつき — dimension_sb4.jpg）
      const ribAngle = bayCenterAngle + baySpread * 0.5;
      const rx1 = domeX + Math.cos(ribAngle) * 140 * scale;
      const ry1 = domeY + Math.sin(ribAngle) * 140 * scale;
      const rx2 = domeX + Math.cos(ribAngle) * 950 * scale;
      const ry2 = domeY + Math.sin(ribAngle) * 950 * scale;

      p.stroke(18, 24, 28, 250);
      p.strokeWeight(22 * scale);
      p.line(rx1, ry1, rx2, ry2);

      p.stroke(255, 242, 195, 215 * glow);
      p.strokeWeight(2.4 * scale);
      p.line(rx1 - 6 * scale, ry1, rx2 - 6 * scale, ry2);
    }

    // 天頂の眩い円形ドーム天窓（Oculus）
    p.fill(255, 252, 238, 250);
    p.stroke(32, 40, 46, 230);
    p.strokeWeight(4 * scale);
    p.circle(domeX, domeY + 110 * scale, 260 * scale);
    p.pop();

    // 3. 天頂から降り注ぐ黄金のボリューメトリック・ゴッドレイ
    ctx.save();
    const rayGrad = ctx.createRadialGradient(
      domeX,
      h * 0.05,
      20 * scale,
      domeX,
      h * 0.5,
      Math.max(w, h) * 0.75
    );
    rayGrad.addColorStop(0, `rgba(255, 252, 230, ${0.55 * glow})`);
    rayGrad.addColorStop(0.5, `rgba(242, 208, 145, ${0.22 * glow})`);
    rayGrad.addColorStop(1, 'rgba(12, 16, 20, 0)');
    ctx.fillStyle = rayGrad;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();

    // 4. 画面に浮かび上がる「白い幾何学花紋（ロゼット）万華鏡オーバーレイ」（dimension_sb4.jpg Row 0 Col 2 / Row 2 Col 0）
    this.drawWhiteKaleidoscopeRosetteOverlay(w, h, scale, glow);
  }

  /**
   * 大聖堂シーンで画面全体に展開する「白い幾何学八弁ロゼット＆万華鏡ライン」（dimension_sb4.jpg Row 0 Col 2 / Row 2 Col 0）
   */
  drawWhiteKaleidoscopeRosetteOverlay(w, h, scale, glow) {
    const p = this.p;
    p.push();
    p.noFill();

    for (let i = 0; i < this.rosetteGrid.length; i++) {
      const rg = this.rosetteGrid[i];
      const rx = rg.u * w - this.parallaxX * 0.25;
      const ry = rg.v * h - this.parallaxY * 0.25;
      const wave = 0.5 + 0.5 * Math.sin(this.time * 2.4 + rg.phase);
      const alpha = (65 + 145 * wave) * glow;
      const r = rg.size * (0.75 + 0.35 * wave);

      p.push();
      p.translate(rx, ry);
      p.rotate(this.time * 0.35 + rg.phase * 0.2);

      // 8弁の白い幾何学ロゼット花紋
      p.stroke(255, 252, 242, alpha);
      p.strokeWeight(1.15 * scale);
      for (let k = 0; k < 4; k++) {
        p.rotate(p.QUARTER_PI);
        p.ellipse(0, 0, r * 1.4, r * 0.42);
      }
      p.circle(0, 0, r * 0.32);
      p.pop();
    }

    p.pop();
  }

  /**
   * Mode 3: 星辰の湾曲地平線と浮遊城塞・黄金の十字星（dimension_maxres1.jpg / dimension_sb1.jpg Row 0 / dimension_sb2.jpg Row 0-2）
   */
  drawMode3CosmicHorizonAndFloatingCitadel(cx, cy, w, h, scale, dt, speedMult, glow) {
    const p = this.p;
    const ctx = p.drawingContext;

    // 1. 「青を背景に 色の無い 遠い」深い藍黒〜琥珀の地平線グラデーション（dimension_maxres1.jpg）
    p.background(6, 12, 18);
    const horizonY = h * 0.72 - this.parallaxY * 0.25;

    ctx.save();
    const skyGrad = ctx.createLinearGradient(0, 0, 0, horizonY);
    skyGrad.addColorStop(0, 'rgba(6, 15, 24, 0.98)');
    skyGrad.addColorStop(0.52, 'rgba(24, 32, 38, 0.92)');
    skyGrad.addColorStop(0.84, `rgba(148, 118, 78, ${0.78 * glow})`);
    skyGrad.addColorStop(1, `rgba(255, 242, 198, ${0.96 * glow})`);
    ctx.fillStyle = skyGrad;
    ctx.fillRect(0, 0, w, horizonY + 40 * scale);
    ctx.restore();

    // 2. 宇宙の星粒と黄金の星雲靄
    this.drawGoldenNebulaAndStars(w, h, scale, glow);

    // 3. 湾曲する惑星の地平線と暗い海面の水面反射（dimension_maxres1.jpg）
    p.push();
    const curveRadius = w * 2.4;
    const curveCenterY = horizonY + curveRadius;

    // 海面の暗いベース
    p.noStroke();
    p.fill(16, 14, 14, 250);
    p.circle(cx, curveCenterY, curveRadius * 2);

    // 地平線のアーク発光ライン
    p.noFill();
    p.stroke(255, 246, 205, 235 * glow);
    p.strokeWeight(3.8 * scale);
    p.circle(cx, curveCenterY, curveRadius * 2);

    // 水面に揺れる黄金の反射さざ波ライン（dimension_maxres1.jpg 中央下）
    const starX = w * 0.5 - this.parallaxX * 0.35;
    for (let r = 0; r < 22; r++) {
      const ry = horizonY + 6 * scale + r * 7.5 * scale;
      if (ry > h) break;
      const spread = (18 + r * 8 + Math.sin(this.time * 3.2 + r) * 14) * scale * (1 - r / 26);
      p.stroke(255, 238, 185, (215 - r * 8) * glow);
      p.strokeWeight((2.2 - r * 0.06) * scale);
      p.line(starX - spread, ry, starX + spread, ry);
    }

    // 4. 地平線上で輝く「黄金の十字星・放射コロナ・天頂まで貫く垂直光線」（dimension_maxres1.jpg 完全再現）
    // 放射コロナの繊細な光条リング
    const coronaRays = 48;
    const coronaR = (78 + Math.sin(this.time * 2.0) * 8) * scale;
    p.stroke(255, 235, 178, 72 * glow);
    p.strokeWeight(1.0 * scale);
    for (let i = 0; i < coronaRays; i++) {
      const a = (p.TWO_PI / coronaRays) * i + this.time * 0.05;
      const rLen = coronaR * (0.65 + 0.35 * Math.sin(i * 5.0 + this.time * 2.5));
      p.line(
        starX + Math.cos(a) * 12 * scale,
        horizonY + Math.sin(a) * 12 * scale,
        starX + Math.cos(a) * rLen,
        horizonY + Math.sin(a) * rLen
      );
    }

    // 画面上端から海面まで垂直に貫く1本の鋭い黄金白線（dimension_maxres1.jpg）
    p.stroke(255, 248, 220, 215 * glow);
    p.strokeWeight(1.6 * scale);
    p.line(starX, 0, starX, h);

    // 地平線に沿う水平の長い十字光芒
    const hFlareW = (240 + this.flareBoost * 90) * scale;
    p.stroke(255, 245, 205, 195 * glow);
    p.strokeWeight(2.4 * scale);
    p.line(starX - hFlareW, horizonY, starX + hFlareW, horizonY);

    p.noStroke();
    p.fill(255, 252, 235, 250);
    p.circle(starX, horizonY, (18 + this.flareBoost * 10) * scale);

    // 5. 宙に浮かぶ「逆さ尖塔を持つ浮遊城塞島シルエット」（dimension_sb2.jpg Row 0-2 完全再現）
    this.drawFloatingCitadelIsland(
      cx,
      h * 0.42 + Math.sin(this.time * 1.4) * 8 * scale,
      scale,
      glow
    );

    p.pop();
  }

  /**
   * 黄金星雲の宙に浮かぶ「上下対称の尖塔を持つ浮遊城塞島」（dimension_sb2.jpg Row 0-2）
   */
  drawFloatingCitadelIsland(x, y, scale, glow) {
    const p = this.p;
    p.push();
    p.translate(x, y);
    p.rotate(Math.sin(this.time * 0.8) * 0.03);

    // 背後の黄金リムライトオーラ
    p.noStroke();
    p.fill(255, 232, 175, 58 * glow);
    p.ellipse(0, 0, 210 * scale, 145 * scale);

    // 浮遊城塞のメインシルエット（上向きの城塔群＋下向きの逆さ岩塊・尖塔）
    p.fill(22, 18, 16, 245);
    p.stroke(248, 222, 168, 185 * glow);
    p.strokeWeight(1.2 * scale);

    p.beginShape();
    // 左端の浮遊岩
    p.vertex(-78 * scale, 0);
    p.vertex(-58 * scale, -14 * scale);
    // 左の城塔
    p.vertex(-44 * scale, -14 * scale);
    p.vertex(-38 * scale, -42 * scale);
    p.vertex(-28 * scale, -18 * scale);
    // 中央の大尖塔（ゴシックキャッスル）
    p.vertex(-16 * scale, -24 * scale);
    p.vertex(0, -68 * scale);
    p.vertex(16 * scale, -24 * scale);
    // 右の城塔
    p.vertex(32 * scale, -46 * scale);
    p.vertex(46 * scale, -14 * scale);
    p.vertex(78 * scale, 0);
    // 下側の逆さ尖塔・鍾乳石状の浮遊岩底面
    p.vertex(52 * scale, 16 * scale);
    p.vertex(36 * scale, 48 * scale);
    p.vertex(18 * scale, 22 * scale);
    p.vertex(4 * scale, 58 * scale);
    p.vertex(-14 * scale, 26 * scale);
    p.vertex(-32 * scale, 52 * scale);
    p.vertex(-52 * scale, 14 * scale);
    p.endShape(p.CLOSE);

    // 左右に浮かぶ小さな衛星岩塊（dimension_sb2.jpg Row 1）
    p.quad(-108 * scale, -4 * scale, -92 * scale, -14 * scale, -84 * scale, -2 * scale, -96 * scale, 10 * scale);
    p.quad(88 * scale, -8 * scale, 106 * scale, -18 * scale, 114 * scale, -4 * scale, 98 * scale, 8 * scale);

    // 城塞内部で灯る小さな黄金の窓明かり
    p.noStroke();
    p.fill(255, 242, 195, 235);
    p.rect(-6 * scale, -28 * scale, 4 * scale, 9 * scale);
    p.rect(4 * scale, -24 * scale, 4 * scale, 8 * scale);
    p.rect(-36 * scale, -18 * scale, 3.5 * scale, 7 * scale);
    p.rect(28 * scale, -20 * scale, 3.5 * scale, 7 * scale);

    p.pop();
  }

  /**
   * 黄金の星雲靄（Nebula）と瞬く星粒の描画
   */
  drawGoldenNebulaAndStars(w, h, scale, glow) {
    const p = this.p;
    p.push();
    p.noStroke();

    // 渦巻く金色の星雲靄
    for (let i = 0; i < this.nebulaClouds.length; i++) {
      const nc = this.nebulaClouds[i];
      nc.x += nc.speed;
      if (nc.x < -250 * scale) nc.x = w + 250 * scale;
      if (nc.x > w + 250 * scale) nc.x = -250 * scale;

      p.push();
      p.translate(nc.x - this.parallaxX * 0.3, nc.y - this.parallaxY * 0.3);
      p.rotate(nc.angle + Math.sin(this.time * 0.5 + nc.phase) * 0.05);
      p.fill(238, 202, 142, nc.alpha * glow);
      p.ellipse(0, 0, nc.w, nc.h);
      p.pop();
    }

    // 瞬く星々と丸ボケ（Bokeh）光粒
    for (let i = 0; i < this.stars.length; i++) {
      const st = this.stars[i];
      const sx = st.x - this.parallaxX * st.depth;
      const sy = st.y - this.parallaxY * st.depth;
      const tw = 0.5 + 0.5 * Math.sin(this.time * st.twinkleSpeed + st.phase);

      if (st.isBokeh) {
        p.fill(255, 235, 185, (38 + 65 * tw) * glow);
        p.circle(sx, sy, st.size * 4.2);
      }
      p.fill(255, 248, 222, (110 + 145 * tw) * glow);
      p.circle(sx, sy, st.size);
    }

    p.pop();
  }

  /**
   * 立ち昇るシャンパンゴールドの光粒と深紅のポピー花弁パーティクルの更新・描画
   */
  updateAndDrawLightParticles(w, h, scale, dt, speedMult, glow) {
    const p = this.p;
    p.push();
    p.noStroke();

    for (let i = 0; i < this.lightMotifParticles.length; i++) {
      const pt = this.lightMotifParticles[i];
      pt.x += (pt.vx + Math.sin(this.time * 2.0 + pt.phase) * 0.45) * speedMult * dt;
      pt.y += pt.vy * speedMult * dt;
      pt.rot += pt.rotSpeed * dt;

      if (pt.y < -20) {
        pt.y = h + 20;
        pt.x = p.random(w);
      }
      if (pt.x < -20) pt.x = w + 20;
      if (pt.x > w + 20) pt.x = -20;

      const px = pt.x - this.parallaxX * pt.depth * 0.5;
      const py = pt.y - this.parallaxY * pt.depth * 0.5;

      if (pt.isCrimsonPetal || (this.modeIndex === 1 && i % 2 === 0)) {
        // 舞い上がる深紅のポピー花弁・紅い光粒
        p.push();
        p.translate(px, py);
        p.rotate(pt.rot);
        p.fill(238, 36, 52, pt.alpha * 0.9);
        p.ellipse(0, 0, pt.size * 1.45, pt.size * 0.85);
        p.pop();
      } else {
        // 温かいシャンパンゴールドの星光粒子
        p.fill(255, 242, 198, pt.alpha * glow);
        p.circle(px, py, pt.size);
      }
    }

    p.pop();
  }

  /**
   * クリック地点に広がる「アストロラーベ（天球儀）の幾何学目盛り波紋」（dimension_sb2.jpg Row 0 Col 0 / dimension_sb9.jpg Row 2）
   */
  updateAndDrawDimensionalRipples(scale, dt, glow) {
    const p = this.p;
    for (let i = this.dimensionalRipples.length - 1; i >= 0; i--) {
      const rp = this.dimensionalRipples[i];
      rp.radius = p.lerp(rp.radius, rp.maxRadius, 0.09 * dt);
      rp.rot += 0.015 * dt;
      rp.alpha -= 3.8 * dt;

      if (rp.alpha <= 0) {
        this.dimensionalRipples.splice(i, 1);
        continue;
      }

      p.push();
      p.translate(rp.x, rp.y);
      p.rotate(rp.rot);
      p.noFill();

      p.stroke(255, 242, 198, rp.alpha * 0.85 * glow);
      p.strokeWeight(1.8 * scale);
      p.circle(0, 0, rp.radius * 2);

      p.stroke(255, 248, 225, rp.alpha * 0.55 * glow);
      p.strokeWeight(1.0 * scale);
      p.circle(0, 0, rp.radius * 1.72);

      // 天球儀の精密な角度目盛りティック
      const ticks = 36;
      for (let t = 0; t < ticks; t++) {
        const a = (p.TWO_PI / ticks) * t;
        const r1 = rp.radius * 0.86;
        const r2 = rp.radius * (t % 3 === 0 ? 1.0 : 0.93);
        p.line(Math.cos(a) * r1, Math.sin(a) * r1, Math.cos(a) * r2, Math.sin(a) * r2);
      }
      p.pop();
    }
  }

  /**
   * カーソル移動軌跡のシャンパンゴールド＆深紅スパーク
   */
  updateAndDrawCursorTrail(scale, dt, glow) {
    if (this.cursorTrail.length === 0) return;
    const p = this.p;
    p.push();
    p.noStroke();

    for (let i = this.cursorTrail.length - 1; i >= 0; i--) {
      const tr = this.cursorTrail[i];
      tr.alpha -= 7.5 * dt;
      if (tr.alpha <= 0) {
        this.cursorTrail.splice(i, 1);
        continue;
      }
      if (tr.isCrimson) {
        p.fill(242, 42, 58, tr.alpha * glow);
      } else {
        p.fill(255, 244, 205, tr.alpha * glow);
      }
      p.circle(tr.x, tr.y, tr.size);
    }

    p.pop();
  }

  /**
   * MV特有のシネマスコープ上下黒帯（レターボックス）
   */
  drawCinemascopeLetterbox(w, h) {
    const p = this.p;
    const barH = Math.round(h * 0.075);
    p.push();
    p.noStroke();
    p.fill(5, 5, 7, 255);
    p.rect(0, 0, w, barH);
    p.rect(0, h - barH, w, barH);
    p.pop();
  }
}
